import { DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT } from 'consts/dateFormats';
import { isDefine } from 'helpers';
import { momentInstance } from 'helpers/date-time';
import { cloneDeep, get, isEmpty, isObject } from 'lodash';
import { timezoneHelpers } from 'providers/TimezoneProvider';
import OptimizationModel from './optimization.model';

export const NUMBER_DID_PER_PAGE = 20;

class DidModel {
  constructor() {
    this.country = '';
    this.created = '';
    this.dids = [];
    this.groupId = 0;
    this.groupName = '';
    this.id = '';
    this.name = '';
    this.routeId = '';
    this.setting = {
      deliveredThresholdRelease: 0,
      campaignCheck: false,
      deliveredThreshold: 0,
      autoRelease: false,
    };
    this.userId = '';
  }

  //! Response
  static parseInfoGroupFromResponse(res) {
    if (isObject(res?.data)) {
      return {
        name: res.data.groupName,
        id: res.data.groupId,
        setting: res?.data?.setting || {},
        countries: res?.data.countries || [],
        country: res?.data?.country || '',
        routeId: res?.data?.routeId || '',
        didBuyable: res?.data?.didBuyable,
        voiceEnabled: res?.data?.voiceEnabled,
      };
    }

    return {};
  }

  //! Request
  static parseInitialAddNewDidGroup() {
    return {
      name: '',
      countries: [],
      routeId: '',
    };
  }

  static parseRequestAddNewDidGroup(values) {
    return {
      name: values?.name?.trim() || '',
      countries: values?.countries.map((elm) => elm.value) || [],
      routeId: values?.routeId || '',
    };
  }

  static parseInitialLoadDidGroups(values = {}) {
    return {
      name: values?.name || '',
      limit: values?.limit || 50,
      offset: values?.offset || 0,
      routeId: values?.routeId || null,
      country: values?.country || '',
      requireDid: values.requireDid || false,
    };
  }

  static parseFiltersTotalContact(values = {}) {
    if (isObject(values)) {
      return {
        country: values?.country || '',
        routeId: values?.routeId || '',
        name: values?.name || '',
      };
    }

    return {};
  }

  static parseRequestLoadDidGroups(values = {}) {
    return {
      name: values?.name || '',
      limit: values?.limit || NUMBER_DID_PER_PAGE,
      offset: values?.offset || 0,
      country: values?.country || '',
      routeId: values.routeId || '',
      requireDid: values.requireDid || false,
    };
  }

  static parseInitialSettingDID(initialSettings) {
    // B1 (TEX-230): the deployed cloud-webapp returns the legacy setting shape
    // (didGroupOptimizationSettings / top-level autoRelease) and drops the new
    // `optimization`/`autoReleaseSetting` keys, so reading only `optimization`
    // showed empty defaults after a save. Prefer the new keys when present
    // (forward-compat), otherwise normalize the legacy shape so the form reloads
    // saved values. Interim mapping — removed at the Go rewrite (decision-50).
    const legacyOpt = initialSettings?.didGroupOptimizationSettings;
    const optimization =
      initialSettings?.optimization ??
      (legacyOpt
        ? {
            enabled: legacyOpt.enable,
            sentPerCycle: legacyOpt.messagePerCycle,
            rotation: legacyOpt.rotation,
            concurrentCheck: legacyOpt.concurrentCheck,
            maxConcurrent: legacyOpt.maxConcurrent,
          }
        : undefined);
    const autoReleaseEnabled =
      initialSettings?.autoReleaseSetting?.enabled ?? initialSettings?.autoRelease ?? false;

    return {
      didGroup: initialSettings?.didGroup,

      optimization: {
        enabled: optimization?.enabled || false,

        enableSentPerCycle: !!optimization?.sentPerCycle,
        sentPerCycle: optimization?.sentPerCycle || 0,

        concurrentCheck: optimization?.concurrentCheck || false,
        rotation: optimization?.rotation || false,
        maxConcurrent: optimization?.maxConcurrent || 0,
        filters: isEmpty(initialSettings?.optimization?.filters)
          ? []
          : OptimizationModel.parseInitialConditions(initialSettings?.optimization?.filters),
      },

      autoReleaseSetting: {
        limit: initialSettings?.autoReleaseSetting?.limit || 0,
        enabled: autoReleaseEnabled,
        filters: isEmpty(initialSettings?.autoReleaseSetting?.filters)
          ? [{ value: '', condition: '', operator: '' }]
          : OptimizationModel.parseInitialConditions(initialSettings?.autoReleaseSetting?.filters),
      },

      forwardCallSetting: {
        enable: !!initialSettings?.forwardCallSetting?.phone,
        phone: initialSettings?.forwardCallSetting?.phone || '',
        workingWeekDays: initialSettings?.forwardCallSetting?.workingWeekDays || [],
        workingHourFrom: initialSettings?.forwardCallSetting?.workingHourFrom ?? '',
        workingHourTo: initialSettings?.forwardCallSetting?.workingHourTo ?? '',
        timezone: initialSettings?.forwardCallSetting?.timezone || 'America/Los_Angeles',
      },

      sleepTimerSetting: {
        chkSleepTimer: get(initialSettings, 'sleepTimerSetting.chkSleepTimer') || false,
        startSleepTime: get(initialSettings, 'sleepTimerSetting.startSleepTime') || '',
        endSleepTime: get(initialSettings, 'sleepTimerSetting.endSleepTime') || '',
        weekDays: get(initialSettings, 'sleepTimerSetting.weekDays') || [],
      },

      scrubSetting: {
        autoScrub: get(initialSettings, 'scrubSetting.autoScrub') || false,
        autoScrubInterval: get(initialSettings, 'scrubSetting.autoScrubInterval') || 1,
        autoRefillBadDid: get(initialSettings, 'scrubSetting.autoRefillBadDid') || false,
        autoMoveBadDid: get(initialSettings, 'scrubSetting.autoMoveBadDid') || false,
        badDidGroupId: isDefine(get(initialSettings, 'scrubSetting.badDidGroupId'))
          ? {
              label: get(initialSettings, 'scrubSetting.badDidGroupName') || '',
              value: get(initialSettings, 'scrubSetting.badDidGroupId') || '',
            }
          : '',
      },
    };
  }

  static parseSaveSettingDID(body) {
    if (isObject(body)) {
      const settings = cloneDeep(body);
      const { optimization } = settings;
      const { enableSentPerCycle } = optimization;

      const result = {
        sleepTimerSetting: settings?.sleepTimerSetting || {},

        optimization: {
          enabled: settings?.optimization?.enabled || false,
          sentPerCycle: enableSentPerCycle ? settings?.optimization?.sentPerCycle : undefined,
          concurrentCheck: settings?.optimization?.concurrentCheck || false,
          rotation: settings?.optimization?.rotation || false,
          maxConcurrent: settings?.optimization?.maxConcurrent || 0,
          filters: OptimizationModel.parseConditions(settings?.optimization?.filters),
        },

        autoReleaseSetting: {
          // limit: settings?.autoReleaseSetting?.limit,
          enabled: settings?.autoReleaseSetting?.enabled || false,
          filters: OptimizationModel.parseConditions(settings?.autoReleaseSetting?.filters),
        },
      };

      // B1 (TEX-230): the deployed cloud-webapp persists + the deployed campaign
      // engine reads the legacy shape; the `optimization`/`autoReleaseSetting` keys
      // above are silently dropped by the old backend. Mirror the scalar knobs into
      // the legacy fields so they actually save and take effect. Kept alongside the
      // new keys for forward-compat. Interim mapping — removed at the Go rewrite
      // (decision-50). Note: the condition builders (optimization.filters /
      // auto-release conditions) are intentionally NOT mapped here — no reliable
      // legacy equivalent; they land with the Go rewrite (TEX-230).
      result.didGroupOptimizationSettings = {
        enable: result.optimization.enabled,
        messagePerCycle: result.optimization.sentPerCycle || 0,
        rotation: result.optimization.rotation,
        concurrentCheck: result.optimization.concurrentCheck,
        maxConcurrent: result.optimization.maxConcurrent,
      };
      result.autoRelease = result.autoReleaseSetting.enabled;

      return result;
    }

    return {};
  }

  static parseRequestToFiltersDIDGroupDetail(filters) {
    // The hour window (dateRanges → startDateHour/endDateHour, YYMMDDHH) only feeds the
    // backend's Bigtable "Recent …" columns, which have no data source in this stack
    // (TEX-71). Worse, the deployed cloud-webapp paginates the result a SECOND time
    // whenever both hours are non-zero (NumberService.loadDidInGroup → ListUtils.getPage
    // on an already-paged list), so every page after the first came back empty
    // ("Data not found", TEX-278). Send 0/0 until TEX-71 ports the DID list; the
    // computation stays so the window can be restored in one line.
    const { from: startDateHour, to: endDateHour } = timezoneHelpers.convertDateRangeByTimezone({
      from: filters?.dateRanges?.from,
      to: filters?.dateRanges?.to,
      format: DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT,
    });
    void startDateHour;
    void endDateHour;

    return {
      id: filters?.id,
      didSearch: filters?.didSearch,
      sortField: filters?.sortField,
      sortDirection: filters?.sortDirection,
      pageSize: filters.size,
      country: filters?.country || null,
      pageNumber: (filters?.page || 0) + 1,
      startDateHour: 0,
      endDateHour: 0,
    };
  }
}

export default DidModel;
